import React, { useState, useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { Form, Input, Button, Flex, Row, Col } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Text, Title } from "ui/Typography"
import { FacebookConnect } from "pagesV2/pro/Settings/LoginAndSecurity/FacebookConnect"
import { GoogleConnect } from "pagesV2/pro/Settings/LoginAndSecurity/GoogleConnect"
import { AppleConnect } from "pagesV2/pro/Settings/LoginAndSecurity/AppleConnect"
import styles from "./index.module.css"
import { toaster } from "components/common/Toast"
import LabeledField from "../ProfileInfo/LabeledField"
import { validateStrongPassword } from "ui/auth/utils"
import { WarningIcon } from "ui/Icon/DSIcon/WarningIcon"
import { colors, spacings } from "themes/variables"
import UpdateEmail from "../../Modals/UpdateEmail"
import useSendOTP from "../../Hooks/useSendOTP"
import * as api from "./ApiInterface"

const LoginAndSecurity = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { loading } = useSelector((state) => state.confirmPassword || {})
  const { email, is_password } = useSelector((state) => state.user)

  // Email update modal state
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const { cooldown, sendCodeLabel, hasSentCode, markSent, resetOTP } = useSendOTP()

  // Ensure fields are empty on mount
  React.useEffect(() => {
    form?.resetFields()
  }, [])

  const initialFormValues = useMemo(() => ({ password: "", current_password: "" }), [])

  const gridGutter = useMemo(() => [24, 16], [])

  const handleUpdatePassword = useCallback(
    async ({ current_password, password }) => {
      const result = await api.changePassword(dispatch, { isPassword: is_password, current_password, password }, t)

      if (!result?.success) {
        if (result?.errorField) {
          form.setFields([{ name: result.errorField.name, errors: [result.errorField.message] }])
        }
        return
      }

      form?.resetFields()
      if (result?.message) toaster.success(result.message)
      if (result?.didSignOut) navigate("/")
    },
    [dispatch, is_password, t, form, navigate]
  )

  // Email: open modal
  const handleOpenEmailModal = useCallback(() => {
    resetOTP()
    setIsEmailModalOpen(true)
  }, [resetOTP])

  // Email: send code
  const handleSendEmailCode = useCallback(
    async (email) => {
      try {
        setIsSendingEmail(true)
        markSent(60)
        const responseMessage = await api.sendOtpToVerifyEmail(dispatch, email)
        if (responseMessage) toaster.success(responseMessage)
      } finally {
        setIsSendingEmail(false)
      }
    },
    [markSent]
  )

  // Email: confirm code
  const handleConfirmEmailCode = useCallback(
    async (email, code) => {
      if (!code || !email) return
      const responseMessage = await api.verifyOtpForEmailUpdation(dispatch, email, code)
      if (responseMessage) toaster.success(responseMessage)
      resetOTP()
      setIsEmailModalOpen(false)
    },
    [dispatch, resetOTP]
  )

  // Email: reset/close
  const handleUpdateEmailReset = useCallback(() => {
    resetOTP()
  }, [resetOTP])

  const handleModalClose = useCallback(() => {
    resetOTP()
    setIsEmailModalOpen(false)
  }, [resetOTP])

  const handleBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  return (
    <>
      <div className={styles.LoginAndSecurityWrapper}>
        {!is_password && (
          <Flex gap={spacings.S} className={`${styles.connectedAccount} px-s py-m mb-xl`}>
            <WarningIcon fill={colors.sportsmanBrand} />
            <Flex vertical gap={spacings.XXS}>
              <Title level={5}>{t("login_security.connected_account")}</Title>
              <Text>{t("login_security.setting_password")}</Text>
            </Flex>
          </Flex>
        )}
        <Form
          form={form}
          name="settings-login-security"
          layout="vertical"
          initialValues={initialFormValues}
          onFinish={handleUpdatePassword}
        >
          <Form.Item label="Email" className="w-1/2">
            <Input value={email || ""} disabled size="large" />
            {!is_password && (
              <Button className="w-full justify-end" type="link" onClick={handleOpenEmailModal}>
                {t("login_security.update_email")}
              </Button>
            )}
          </Form.Item>

          <div className={`w-1/2 ${styles.passwordFields}`}>
            {is_password ? (
              <>
                <LabeledField
                  name="current_password"
                  labelKey="change_password.current_password"
                  rules={[{ required: true, message: t("change_password.current_password_required") }]}
                >
                  <Input.Password placeholder={t("change_password.placeholder")} size="large" />
                </LabeledField>

                <LabeledField
                  name="password"
                  labelKey="new_password.input_label"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.reject(new Error(t("new_password.required")))
                        }
                        return validateStrongPassword(value, t)
                      }
                    }
                  ]}
                >
                  <Input.Password placeholder={t("create_password.placeholder")} size="large" />
                </LabeledField>
              </>
            ) : (
              <LabeledField
                labelKey="login_security.set_password.label"
                name="password"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.reject(new Error(t("login_security.set_password.required")))
                      }
                      return validateStrongPassword(value, t)
                    }
                  }
                ]}
              >
                <Input.Password placeholder={t("login_security.set_password.placeholder")} />
              </LabeledField>
            )}
          </div>
          <div className="mb-xl mt-xs">
            <Text type="secondary">{t("settings.connect_accounts")}</Text>
          </div>
          <Row gutter={gridGutter} className="mb-3xl w-full px-xs">
            <Col sm={12} md={8} span={24}>
              <FacebookConnect />
            </Col>
            <Col sm={12} md={8} span={24}>
              <GoogleConnect />
            </Col>
            <Col sm={12} md={8} span={24}>
              <AppleConnect />
            </Col>
          </Row>
          <Flex gap={16} className={styles.formActions}>
            <Button htmlType="button" variant="outlined" color="primary" onClick={handleBack} className="p-l">
              {t("global.cancel")}
            </Button>
            <Form.Item shouldUpdate>
              {() => (
                <Button
                  htmlType="submit"
                  type="primary"
                  color="primary"
                  loading={loading}
                  disabled={loading || !form.isFieldsTouched(true) || !!form.getFieldsError().filter(({ errors }) => errors.length).length}
                  className="p-l"
                >
                  {t("client.profile.tabs.profile.save_changes")}
                </Button>
              )}
            </Form.Item>
          </Flex>
        </Form>
      </div>
      {isEmailModalOpen && (
        <UpdateEmail
          open={isEmailModalOpen}
          setOpen={setIsEmailModalOpen}
          onClose={handleModalClose}
          hasSentCode={hasSentCode}
          cooldown={cooldown}
          isSending={isSendingEmail}
          sendCodeLabel={sendCodeLabel}
          handleSendCode={handleSendEmailCode}
          handleConfirmCode={handleConfirmEmailCode}
          handleUpdateReset={handleUpdateEmailReset}
          existingEmail={email}
        />
      )}
    </>
  )
}

export default LoginAndSecurity
